import { computeDeploymentCost } from '@neopolis/engine';
import type { Content, Equipment, GameState } from '@neopolis/engine';

import { T } from '../../textes-interface.js';

/** Le schéma de la chaîne, généré depuis ce que l'îlot a réellement posé. */
function SchemaDeChaine({ content, partie }: { content: Content; partie: GameState }) {
  const parId = new Map(content.equipements.map((e) => [e.id, e]));
  const cases = content.chapitres.find((c) => c.id === 'ch2')?.cases_chaine ?? [];

  return (
    <ol className="grid gap-2 sm:grid-cols-4">
      {cases.map((definition, index) => {
        const equipements = partie.chain[definition.id]
          .map((id) => parId.get(id))
          .filter((e): e is Equipment => e !== undefined);

        return (
          <li
            key={definition.id}
            className="rounded-xl border-2 border-slate-300 p-3 print:break-inside-avoid"
          >
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
              {index + 1}. {definition.nom}
            </p>
            {equipements.length === 0 ? (
              <p className="mt-1 text-slate-400">—</p>
            ) : (
              <ul className="mt-1 space-y-1">
                {equipements.map((equipement) => (
                  <li key={equipement.id} className="font-medium text-slate-900">
                    <span aria-hidden="true">{equipement.icone} </span>
                    {equipement.nom}
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * La fiche technique du lampadaire intelligent : trace écrite du chapitre 2.
 *
 * Le schéma et le bilan des jauges sont générés depuis la partie ; la conversion
 * d'énergie et la comparaison sont écrites par l'îlot.
 */
export function FicheTechnique({
  content,
  partie,
  onEcrireTrace,
  onFermer,
}: {
  content: Content;
  partie: GameState;
  onEcrireTrace: (fieldId: string, valeur: string) => void;
  onFermer: () => void;
}) {
  const chapitre = content.chapitres.find((c) => c.id === 'ch2');
  const solution = content.solutions.find((s) => s.id === partie.deployedSolutionId) ?? null;
  const avant = partie.gaugesBeforeDeploy;
  const cout = computeDeploymentCost(partie, content);

  const champ = (id: string) => chapitre?.trace_ecrite.champs.find((f) => f.id === id);
  const champEnergie = champ('chaine_energie');
  const champComparaison = champ('comparaison');

  return (
    <article className="rounded-2xl border-2 border-slate-300 bg-white p-5 print:border-0 print:p-0">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">{chapitre?.trace_ecrite.titre}</h2>
          <p className="text-sm text-slate-600">
            {T.rapport.ilot} {partie.islandCode}
          </p>
        </div>

        <div className="flex gap-2 print:hidden">
          <button
            type="button"
            onClick={() => globalThis.print()}
            className="min-h-touch rounded-xl bg-sky-600 px-4 font-semibold text-white hover:bg-sky-700"
          >
            {T.rapport.imprimer}
          </button>
          <button
            type="button"
            onClick={onFermer}
            className="min-h-touch rounded-xl bg-slate-100 px-4 font-semibold text-slate-700"
          >
            {T.rapport.fermer}
          </button>
        </div>
      </header>

      <section className="mt-5">
        <h3 className="font-bold text-slate-900">{T.fiche.schema}</h3>
        <div className="mt-2">
          <SchemaDeChaine content={content} partie={partie} />
        </div>
      </section>

      {solution && (
        <section className="mt-5 flex flex-wrap gap-x-8 gap-y-2">
          <p>
            <span className="text-sm text-slate-600">{T.fiche.solutionRetenue} : </span>
            <span className="font-semibold text-slate-900">
              {'★'.repeat(solution.etoiles)}
              <span className="sr-only">{`${solution.etoiles} / 3`}</span>
            </span>
          </p>
          <p>
            <span className="text-sm text-slate-600">{T.fiche.coutPaye} : </span>
            <span className="font-semibold tabular-nums text-slate-900">
              {cout} {content.meta.unite_budget}
            </span>
          </p>
        </section>
      )}

      {champEnergie && (
        <section className="mt-5">
          <label htmlFor="trace-energie" className="block font-bold text-slate-900">
            {champEnergie.label}
          </label>
          <textarea
            id="trace-energie"
            rows={2}
            value={partie.writtenTrace.ch2['chaine_energie'] ?? ''}
            onChange={(evenement) => onEcrireTrace('chaine_energie', evenement.target.value)}
            className="mt-1 w-full rounded-xl border-2 border-slate-300 p-3 focus:border-sky-600 print:border-0 print:p-0"
          />
        </section>
      )}

      {avant && (
        <section className="mt-5 overflow-x-auto">
          <h3 className="font-bold text-slate-900">{T.fiche.avantApres}</h3>
          <table className="mt-2 w-full border-collapse text-left text-sm">
            <thead>
              <tr className="border-b-2 border-slate-300">
                <th className="py-2 pr-3">{T.fiche.colonneJauge}</th>
                <th className="py-2 pr-3 text-right">{T.fiche.colonneAvant}</th>
                <th className="py-2 pr-3 text-right">{T.fiche.colonneApres}</th>
                <th className="py-2 text-right">{T.fiche.colonneEcart}</th>
              </tr>
            </thead>
            <tbody>
              {content.jauges.map((jauge) => {
                const debut = avant[jauge.id];
                const fin = partie.gauges[jauge.id];
                const ecart = fin - debut;
                const bonneDirection = jauge.sens === 'haut_meilleur' ? ecart > 0 : ecart < 0;

                return (
                  <tr key={jauge.id} className="border-b border-slate-200">
                    <td className="py-2 pr-3 font-medium text-slate-900">
                      <span aria-hidden="true">{jauge.icone} </span>
                      {jauge.nom}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums text-slate-700">{debut}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-slate-900">{fin}</td>
                    <td
                      className={`py-2 text-right font-semibold tabular-nums ${
                        ecart === 0
                          ? 'text-slate-500'
                          : bonneDirection
                            ? 'text-emerald-700'
                            : 'text-rose-700'
                      }`}
                    >
                      {ecart > 0 ? `+${ecart}` : ecart}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      {champComparaison && (
        <section className="mt-5">
          <label htmlFor="trace-comparaison" className="block font-bold text-slate-900">
            {champComparaison.label}
          </label>
          <textarea
            id="trace-comparaison"
            rows={4}
            value={partie.writtenTrace.ch2['comparaison'] ?? ''}
            onChange={(evenement) => onEcrireTrace('comparaison', evenement.target.value)}
            className="mt-1 w-full rounded-xl border-2 border-slate-300 p-3 focus:border-sky-600 print:border-0 print:p-0"
          />
        </section>
      )}

      <section className="mt-5 rounded-xl bg-amber-50 p-4 print:border print:border-slate-300">
        <h3 className="text-sm font-bold uppercase tracking-wide text-amber-800">
          {T.rapport.aRetenir}
        </h3>
        <p className="mt-1 text-slate-800">{chapitre?.trace_ecrite.a_retenir}</p>
      </section>
    </article>
  );
}
